import React from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import BoltIcon from '@mui/icons-material/Bolt';
import { motion } from 'framer-motion';
import { RandomAvatar } from 'react-random-avatar';
import { getApiUrl } from '../apiUrl';

const API_URL = getApiUrl();

function formatCount(count) {
  if (count === undefined || count === null) return '0';
  if (count === 0) return '0';
  if (count >= 1000) {
    const k = (count / 1000).toFixed(1);
    return k.endsWith('.0') ? `${k.slice(0, -2)}K` : `${k}K`;
  }
  return String(count);
}

function getImageUrl(url) {
  if (!url) return '';
  if (url.includes('/api/images/')) return url;
  if (url.includes('.r2.dev/')) {
    const keyMatch = url.match(/r2\.dev\/(.+)$/);
    if (keyMatch) return `${API_URL}/api/images/${encodeURIComponent(keyMatch[1])}`;
  }
  if (url.includes('events/')) {
    const keyMatch = url.match(/(?:fire-sales-images\/)?(events\/.+)$/);
    if (keyMatch) return `${API_URL}/api/images/${encodeURIComponent(keyMatch[1])}`;
  }
  return url;
}

const pop = {
  initial: { scale: 0.88, opacity: 0.7 },
  animate: { scale: 1, opacity: 1 },
  transition: { duration: 0.2 }
};

export default function UserIdentity({
  user,
  size = 'md',
  variant = 'dark',
  showStats = true,
  badge = null,
  dense = false
}) {
  if (!user || typeof user === 'string') {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <Avatar sx={{ width: size === 'sm' ? 24 : size === 'lg' ? 40 : 32, height: size === 'sm' ? 24 : size === 'lg' ? 40 : 32 }}>
          ?
        </Avatar>
        <Typography variant="body2" sx={{ color: variant === 'dark' ? 'white' : 'text.primary', fontWeight: 600 }}>
          Unknown
        </Typography>
      </Box>
    );
  }

  const avatarSize = size === 'sm' ? 24 : size === 'lg' ? 40 : 32;
  const username = user.username || 'User';
  const helpful = user.helpfulCount ?? 0;
  const hearts = user.lifetimeHearts ?? user.heartsReceived ?? 0;
  const textColor = variant === 'dark' ? 'white' : 'text.primary';
  const mutedColor = variant === 'dark' ? 'rgba(255,255,255,0.65)' : 'text.secondary';

  let avatarNode;
  if (user.profilePhoto) {
    avatarNode = <Avatar src={getImageUrl(user.profilePhoto)} sx={{ width: avatarSize, height: avatarSize }} />;
  } else if (user.avatarSeed) {
    avatarNode = (
      <Avatar sx={{ width: avatarSize, height: avatarSize }}>
        <RandomAvatar seed={user.avatarSeed} size={Math.max(4, Math.round(avatarSize / 8))} />
      </Avatar>
    );
  } else {
    avatarNode = (
      <Avatar sx={{ width: avatarSize, height: avatarSize, bgcolor: '#331B65', fontSize: avatarSize * 0.4 }}>
        {username.charAt(0).toUpperCase()}
      </Avatar>
    );
  }

  return (
    <Box display="flex" alignItems="center" gap={dense ? 0.75 : 1} sx={{ minWidth: 0 }}>
      {avatarNode}
      <Box sx={{ minWidth: 0 }}>
        <Box display="flex" alignItems="center" gap={0.75}>
          <Typography
            variant="body2"
            sx={{
              color: textColor,
              fontWeight: 700,
              fontSize: size === 'sm' ? '0.75rem' : '0.875rem',
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {username}
          </Typography>
          {badge}
        </Box>
        {showStats && (
          <Box display="flex" alignItems="center" gap={0.75}>
            <Box display="flex" alignItems="center" gap={0.15}>
              <BoltIcon sx={{ fontSize: size === 'sm' ? 11 : 13, color: '#81c784' }} />
              <Typography variant="caption" sx={{ color: '#81c784', fontSize: size === 'sm' ? '0.65rem' : '0.7rem', fontWeight: 600 }}>
                <motion.span key={`h-${helpful}`} {...pop}>{formatCount(helpful)}</motion.span>
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.25}>
              <FavoriteIcon sx={{ fontSize: size === 'sm' ? 10 : 12, color: '#e57373' }} />
              <Typography variant="caption" sx={{ color: mutedColor, fontSize: size === 'sm' ? '0.65rem' : '0.7rem', fontWeight: 500 }}>
                <motion.span key={`l-${hearts}`} {...pop}>{formatCount(hearts)}</motion.span>
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export { formatCount, getImageUrl };
